import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Share2, Save, Clock, FlaskConical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { savePreset, listPresets, deletePreset } from "../../lib/api";
import { formatClock } from "../../lib/eclipse";

export default function PresetShareModal({ state, onLoad }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) listPresets().then(setSaved).catch(() => {});
  }, [open]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Give your observation a title"); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        author: author.trim() || "Anonymous Observer",
        time_minutes: state.timeMin,
        danjon: state.danjon,
        bortle: state.bortle,
        viewport: state.viewport,
      };
      const rec = await savePreset(payload);
      toast.success("Observation preset saved");
      setTitle(""); setAuthor("");
      try { setSaved(await listPresets()); } catch { setSaved((s) => [rec, ...s]); }
    } catch (e) {
      toast.error("Could not save preset");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deletePreset(id);
      setSaved((s) => s.filter((p) => p.id !== id));
      toast.success("Preset removed");
    } catch { toast.error("Could not delete preset"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="action-btn" data-testid="open-share-modal" title="Save / Share Scene">
          <Share2 size={15} /><span className="hidden lg:inline">Presets</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#080c14] border-orange-500/25 text-slate-200 max-w-lg" data-testid="preset-share-modal">
        <DialogHeader>
          <DialogTitle className="font-cinzel tracking-widest uppercase text-slate-200 flex items-center gap-2">
            <FlaskConical size={16} className="text-orange-400" /> Observation Presets
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <div className="text-[11px] text-slate-400 font-mono bg-black/40 rounded-lg p-2.5 border border-white/5">
            {formatClock(state.timeMin)} EDT · Danjon L{state.danjon.toFixed(1)} · {state.bortle <= 3 ? "Rural B1" : "City B8"} · {state.viewport}
          </div>
          <Input data-testid="preset-title-input" placeholder="Title (e.g. Peak Totality — Copper)" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-black/40 border-white/10" />
          <Input data-testid="preset-author-input" placeholder="Your name (optional)" value={author} onChange={(e) => setAuthor(e.target.value)} className="bg-black/40 border-white/10" />
          <Button data-testid="save-preset-btn" onClick={handleSave} disabled={busy} className="w-full bg-orange-600 hover:bg-orange-500 text-white font-cinzel tracking-wider">
            <Save size={15} className="mr-2" /> {busy ? "Saving…" : "Save This Observation"}
          </Button>
        </div>

        <div className="mt-2 max-h-[220px] overflow-y-auto space-y-1.5 pr-1" data-testid="preset-list">
          {saved.length === 0 && <p className="text-xs text-slate-500 text-center py-4">No saved observations yet.</p>}
          {saved.map((p) => (
            <div
              key={p.id}
              data-testid={`load-preset-${p.id}`}
              onClick={() => { onLoad(p); setOpen(false); toast.success(`Loaded “${p.title}”`); }}
              className="group relative w-full text-left rounded-lg p-2.5 pr-9 bg-black/30 hover:bg-orange-500/10 border border-white/5 hover:border-orange-500/30 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-200 font-cinzel tracking-wide">{p.title}</span>
                <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock size={10} />{formatClock(p.time_minutes)}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">by {p.author} · L{p.danjon?.toFixed?.(1) ?? p.danjon} · {p.bortle <= 3 ? "Rural" : "City"}</div>
              <button
                data-testid={`delete-preset-${p.id}`}
                onClick={(e) => handleDelete(e, p.id)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 opacity-70 hover:opacity-100 transition"
                title="Delete preset"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
